import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { ArchiveIcon, CalendarIcon, HistoryIcon, TargetIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Author,
	Callout,
	Container,
	FlexGap,
	PAGE_CONTENT_WIDTH,
	Paragraph,
	Pill,
	QuillText,
	Title,
	Todo,
	tokens,
} from '@treams/treams-ui';

import { FeasibilityTag } from './Goal';
import Subgoals, { SubgoalsContext } from './subgoals/Subgoals';
import SubgoalsContainer from './subgoals/SubgoalsContainer';

const Strikethrough = styled.span`
	text-decoration: line-through;
	color: ${tokens.text.secondary};
`;

export default function GoalRevision(props: {
	userID?: string;
	goalID: string;
	revision: string;
	secret?: string;
}) {
	const query = graphql.useGoalRevisionQuery({
		variables: { id: props.goalID, revision: props.revision, secret: props.secret },
	});

	const { localize } = useLocalize();
	const { t } = useTranslation();

	const revision = query.data?.goalRevision;
	const isArchived = !!revision?.archivedAt;

	return (
		<>
			<Container py={4} wide={PAGE_CONTENT_WIDTH}>
				{isArchived ? (
					<Callout
						mb={`${tokens.gap.list}px`}
						color="blue"
						icon={<ArchiveIcon />}
						title={t('goals.show.archived.title')}
						description={t('goals.show.archived.description')}
					/>
				) : (
					<Callout
						mb={`${tokens.gap.list}px`}
						color="blue"
						icon={<HistoryIcon />}
						title={t('goals.show.revision.title')}
						description={t('goals.show.revision.description')}
					/>
				)}
				{revision ? (
					<Box mt={`${tokens.gap.section}px`}>
						<FlexGap alignItems="center" mb={`${tokens.gap.list}px`} gap={tokens.gap.element}>
							<Box flexGrow={0} flexShrink={0}>
								<Todo checked={revision.progress === 1.0} size={32} />
							</Box>
							<Title>
								{revision.completedAt ? (
									<Strikethrough>{localize(revision.title).text}</Strikethrough>
								) : (
									localize(revision.title).text
								)}
							</Title>
						</FlexGap>
						<Box mb={`${tokens.gap.list}px`}>
							<QuillText value={revision.description} />
						</Box>
						<FlexGap mb={`${tokens.gap.list}px`} gap={tokens.gap.element} flexWrap="wrap">
							{revision?.dueDate && (
								<Pill>
									<CalendarIcon />
									{moment(revision.dueDate).format('LL')}
								</Pill>
							)}
							{revision?.feasibility && <FeasibilityTag feasibility={revision.feasibility} />}
							{revision?.competency && (
								<Pill>
									<TargetIcon />
									{localize(revision.competency.competency.title).text}
								</Pill>
							)}
						</FlexGap>
					</Box>
				) : (
					<Box mt={`${tokens.gap.section}px`}>
						<Title.Skeleton mb={`${tokens.gap.list}px`} />
						<Paragraph.Skeleton mb={`${tokens.gap.list}px`} />
						<Author.Skeleton />
					</Box>
				)}
			</Container>
			<SubgoalsContainer>
				{query.data ? (
					<SubgoalsContext goal={revision as unknown as graphql.Goal} />
				) : (
					<Subgoals.Skeleton />
				)}
			</SubgoalsContainer>
		</>
	);
}
